import React, { useState, createContext } from "react"
import type { ReactNode } from "react"
import cx from "classnames"

const HORIZONTAL_SIZES = {
  sm: "text-xs h-8 rounded-sm",
  md: "text-sm h-10 rounded",
  lg: "text-sm h-12 rounded-lg"
}

const VERTICAL_SIZES = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-sm"
}

export type ButtonGroupContextValue = {
  present?: boolean
  groupSize?: string
  activeId?: string | null
  setActiveId?: React.Dispatch<React.SetStateAction<string | null>>
  orientation?: "horizontal" | "vertical"
}

export const ButtonGroupContext = createContext<ButtonGroupContextValue>({})

type ButtonGroupOwnProps = {
  /** Gap and padding size between buttons */
  spacing?: number | string
  /** Layout direction of the group */
  orientation?: "horizontal" | "vertical"
  /** Size of the button group */
  size?: keyof typeof HORIZONTAL_SIZES
  /** Additional CSS classes */
  className?: string
  /** Button elements */
  children?: ReactNode
}

export type ButtonGroupProps = ButtonGroupOwnProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, keyof ButtonGroupOwnProps>

function ButtonGroup({
  children,
  className,
  spacing = 1,
  orientation = "horizontal",
  size = "md",
  ...props
}: ButtonGroupProps) {
  const [activeId, setActiveId] = useState<string | null>(null)

  const classes = cx(
    "inline-grid auto-cols-fr auto-rows-fr items-center rounded overflow-hidden bg-gray-900/[0.06]",
    className,
    `gap-${spacing} p-${spacing}`,
    {
      "grid-flow-col": orientation === "horizontal",
      "grid-flow-row": orientation === "vertical",
      [HORIZONTAL_SIZES[size]]: orientation === "horizontal",
      [VERTICAL_SIZES[size]]: orientation === "vertical"
    }
  )

  const contextValue: ButtonGroupContextValue = {
    present: true,
    groupSize: `${orientation}-${size}`,
    activeId,
    setActiveId,
    orientation
  }

  return (
    <div {...props} className={classes} data-testid="btn-group" role="group">
      <ButtonGroupContext.Provider value={contextValue}>
        {children}
      </ButtonGroupContext.Provider>
    </div>
  )
}

export default ButtonGroup
